import React, { useState, useEffect } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { Calendar, MapPin, Clock, Users, X, AlertCircle, CheckCircle, Search, RefreshCw, Route, Target, Award, Trophy, Plus, Filter } from "lucide-react";
import Navigation from "./Navigation";
import { useAuth } from "../contexts/AuthContext";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { useFetch, useApi } from "../hooks/useApi";
import { convertUTCToMeetingTimezone } from "../utils/dateTimeUtils";
import { apiService } from "../services/apiService";
import MeetingCard from "./ui/MeetingCard";
import { getPrimaryImageUrl } from "../utils/imageUtils";
import EventCreationForm from "./EventCreationForm";
import FilterModal from "./ui/FilterModal";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";

const MyEventsPage = () => {
  const { user, loading } = useAuth();
  const location = useLocation();
  const [upcomingMeetings, setUpcomingMeetings] = useState([]);
  const [activeTab, setActiveTab] = useState("upcoming");
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [showEventForm, setShowEventForm] = useState(false);
  const cardsPerPage = 6;

  // Filter state
  const [searchCity, setSearchCity] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [selectedFilters, setSelectedFilters] = useState([]);
  const [selectedTimeFilter, setSelectedTimeFilter] = useState("All");
  const [showFilterModal, setShowFilterModal] = useState(false);

  // Use new hooks for data fetching
  const { data: meetings, loading: meetingsLoading, refetch: refetchMeetings } = useFetch(
    () => apiService.meetings.getAll({ club_id: user?.club_id }),
    [user?.club_id],
    { enabled: !!user?.club_id }
  );

  // Use new API hook for deleting meetings
  const { execute: executeDelete, loading: deleteLoading } = useApi();

  // State for delete dialog
  const [deleteDialogState, setDeleteDialogState] = useState({
    open: false,
    meetingId: null,
    isRecurring: false,
  });

  // Filter constants
  const runFilters = ["5K", "10K", "Social", "Tempo", "Long Run", "Intervals", "Recovery", "Track"];
  const timeFilters = ["All", "Today", "Tomorrow", "This Week", "Next Week", "Recent"];

  // Popular cities for autocomplete
  const POPULAR_CITIES = [
    'New York, NY',
    'Los Angeles, CA',
    'Chicago, IL',
    'Houston, TX',
    'Phoenix, AZ',
    'Philadelphia, PA',
    'San Antonio, TX',
    'San Diego, CA',
    'Dallas, TX',
    'San Jose, CA',
    'Austin, TX',
    'Jacksonville, FL',
    'Fort Worth, TX',
    'Columbus, OH',
    'Charlotte, NC',
    'San Francisco, CA',
    'Indianapolis, IN',
    'Seattle, WA',
    'Denver, CO',
    'Washington, DC',
    'Boston, MA',
    'El Paso, TX',
    'Nashville, TN',
    'Detroit, MI',
    'Oklahoma City, OK',
    'Portland, OR',
    'Las Vegas, NV',
    'Memphis, TN',
    'Louisville, KY',
    'Baltimore, MD',
    'Milwaukee, WI',
    'Albuquerque, NM',
    'Tucson, AZ',
    'Fresno, CA',
    'Sacramento, CA',
    'Mesa, AZ',
    'Kansas City, MO',
    'Atlanta, GA',
    'Long Beach, CA',
    'Colorado Springs, CO',
    'Raleigh, NC',
    'Miami, FL',
    'Virginia Beach, VA',
    'Omaha, NE',
    'Oakland, CA',
    'Minneapolis, MN',
    'Tulsa, OK',
    'Arlington, TX',
    'Tampa, FL',
    'New Orleans, LA'
  ];

  const handleDeleteMeeting = async (meetingId) => {
    // Find the meeting to check if it's recurring
    const meeting = meetings?.find(m => m.id === meetingId);
    const isRecurring = meeting?.is_recurring || false;

    if (isRecurring) {
      // Show dialog with options for recurring meetings
      setDeleteDialogState({
        open: true,
        meetingId,
        isRecurring: true,
      });
    } else {
      // For non-recurring meetings, show simple confirmation
      if (!window.confirm('Are you sure you want to delete this meeting? This action cannot be undone.')) {
        return;
      }

      try {
        await executeDelete(
          () => apiService.meetings.delete(meetingId, false),
          { 
            showSuccessToast: true,
            successMessage: 'Meeting deleted successfully'
          }
        );
        refetchMeetings(); // Refresh the data
      } catch (error) {
        // Error is already handled by useApi hook
      }
    }
  };

  const executeDeleteWithOption = async (deleteEntireSeries = false) => {
    const { meetingId } = deleteDialogState;
    if (!meetingId) return;

    try {
      await executeDelete(
        () => apiService.meetings.delete(meetingId, deleteEntireSeries),
        { 
          showSuccessToast: true,
          successMessage: deleteEntireSeries 
            ? 'Meeting series deleted successfully'
            : 'Meeting deleted successfully'
        }
      );
      setDeleteDialogState({ open: false, meetingId: null, isRecurring: false });
      refetchMeetings(); // Refresh the data
    } catch (error) {
      // Error is already handled by useApi hook
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      upcoming: { color: "bg-green-500/20 text-green-300 border-green-500/30", icon: CheckCircle },
      completed: { color: "bg-blue-500/20 text-blue-300 border-blue-500/30", icon: CheckCircle },
      cancelled: { color: "bg-red-500/20 text-red-300 border-red-500/30", icon: X }
    };
    
    const config = statusConfig[status] || statusConfig.upcoming;
    const IconComponent = config.icon;
    
    return (
      <Badge className={`${config.color} text-runcards-xs`}>
        <IconComponent className="w-3 h-3 mr-1" />
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    );
  };

  // Helper function to format UTC datetime to meeting timezone (12-hour time)
  const formatScheduledDateToLocalTime = (scheduledDate, meetingTimezone = null) => {
    if (!scheduledDate) return '';
    
    try {
      const meetingDate = convertUTCToMeetingTimezone(scheduledDate, meetingTimezone);
      if (!meetingDate || isNaN(meetingDate.getTime())) {
        return '';
      }
      
      const hours = meetingDate.getHours();
      const minutes = meetingDate.getMinutes();
      const period = hours >= 12 ? 'PM' : 'AM';
      const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
      
      return `${displayHours}:${minutes.toString().padStart(2, '0')} ${period}`;
    } catch (error) {
      return '';
    }
  };

  // Helper function to format date in meeting timezone
  const formatDateInMeetingTimezone = (scheduledDate, meetingTimezone = null) => {
    if (!scheduledDate) return '';
    
    try {
      const meetingDate = convertUTCToMeetingTimezone(scheduledDate, meetingTimezone);
      if (!meetingDate || isNaN(meetingDate.getTime())) {
        return '';
      }
      
      return meetingDate.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        timeZone: meetingTimezone || undefined
      });
    } catch (error) {
      return '';
    }
  };

  // Helper function to parse scheduled date
  const parseScheduledDate = (scheduledDate) => {
    if (!scheduledDate) {
      throw new Error('Missing scheduled date');
    }
    
    const date = new Date(scheduledDate);
    
    if (isNaN(date.getTime())) {
      throw new Error('Invalid scheduled date format');
    }
    
    return date;
  };

  // Categorize meetings based on database status only
  const getCategorizedMeetings = () => {
    const allMeetings = meetings || [];
    
    if (!allMeetings || allMeetings.length === 0) {
      return { upcoming: [], cancelled: [], recent: [] };
    }
    
    // Filter by database status only - no time calculations
    const upcoming = allMeetings.filter(meeting => 
      meeting.status === 'upcoming'
    );
    
    const cancelled = allMeetings.filter(meeting => 
      meeting.status === 'cancelled'
    );
    
    const recent = allMeetings.filter(meeting => 
      meeting.status === 'completed'
    );
    
    return { upcoming, cancelled, recent };
  };

  // Apply additional filters to meetings
  const applyFilters = (meetings) => {
    if (!meetings || meetings.length === 0) return meetings;

    return meetings.filter(meeting => {
      // City filter
      if (searchCity.trim()) {
        const searchCityLower = searchCity.toLowerCase().trim();
        const meetingLocation = meeting.address?.toLowerCase() || '';
        
        if (!meetingLocation.includes(searchCityLower)) {
          return false;
        }
      }

      // Time filter
      if (selectedTimeFilter !== "All") {
        const meetingDate = new Date(meeting.scheduled_date);
        const today = new Date();
        const tomorrow = new Date(today);
        tomorrow.setDate(tomorrow.getDate() + 1);
        const nextWeek = new Date(today);
        nextWeek.setDate(nextWeek.getDate() + 7);
        const twoWeeksFromNow = new Date(today);
        twoWeeksFromNow.setDate(twoWeeksFromNow.getDate() + 14);

        if (selectedTimeFilter === "Today") {
          const isToday = meetingDate.toDateString() === today.toDateString();
          if (!isToday) return false;
        } else if (selectedTimeFilter === "Tomorrow") {
          const isTomorrow = meetingDate.toDateString() === tomorrow.toDateString();
          if (!isTomorrow) return false;
        } else if (selectedTimeFilter === "This Week") {
          if (meetingDate < today || meetingDate > nextWeek) return false;
        } else if (selectedTimeFilter === "Next Week") {
          if (meetingDate < nextWeek || meetingDate > twoWeeksFromNow) return false;
        } else if (selectedTimeFilter === "Recent") {
          // For recent, we want completed meetings from the last 30 days
          const thirtyDaysAgo = new Date(today);
          thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
          if (meetingDate < thirtyDaysAgo || meeting.status !== 'completed') return false;
        }
      }

      // Type filter
      if (selectedFilters.length > 0) {
        return selectedFilters.some(filter => {
          const filterLower = filter.toLowerCase();
          const meetingName = meeting.name?.toLowerCase() || '';
          const meetingDescription = meeting.what_to_expect?.toLowerCase() || '';
          
          // Check if filter matches name or description
          return meetingName.includes(filterLower) || 
                 meetingDescription.includes(filterLower);
        });
      }

      return true;
    });
  };

  const filterMeetingsBySearch = (meetings) => {
    if (!meetings || !searchTerm.trim()) {
      return meetings || [];
    }
    
    const searchLower = searchTerm.toLowerCase();
    return meetings.filter(meeting => {
      const name = meeting.name?.toLowerCase() || '';
      const location = meeting.address?.toLowerCase() || '';
      const description = meeting.what_to_expect?.toLowerCase() || '';
      const scheduledDate = meeting.scheduled_date?.toLowerCase() || '';
      
      return name.includes(searchLower) || 
             location.includes(searchLower) || 
             description.includes(searchLower) ||
             scheduledDate.includes(searchLower);
    });
  };

  // Filter helper functions
  const generateSuggestions = (query) => {
    if (!query || query.length < 2) return [];
    
    return POPULAR_CITIES.filter(city => 
      city.toLowerCase().includes(query.toLowerCase())
    ).slice(0, 5); // Limit to 5 suggestions
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchCity(value);
    
    if (value.length >= 2) {
      const newSuggestions = generateSuggestions(value);
      setSuggestions(newSuggestions);
      setShowSuggestions(newSuggestions.length > 0);
    } else {
      setShowSuggestions(false);
      setSuggestions([]);
    }
  };

  const handleSuggestionSelect = (city) => {
    setSearchCity(city);
    setShowSuggestions(false);
    setSuggestions([]);
  };

  const handleInputFocus = () => {
    if (searchCity.length >= 2) {
      const newSuggestions = generateSuggestions(searchCity);
      setSuggestions(newSuggestions);
      setShowSuggestions(newSuggestions.length > 0);
    }
  };

  const handleInputBlur = () => {
    setTimeout(() => {
      setShowSuggestions(false);
    }, 200);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      // Search functionality is handled by the filter logic
    }
  };

  const toggleFilter = (filter) => {
    setSelectedFilters(prev => 
      prev.includes(filter) 
        ? prev.filter(f => f !== filter)
        : [...prev, filter]
    );
  };

  const clearAllFilters = () => {
    setSelectedFilters([]);
    setSelectedTimeFilter("All");
    setSearchCity("");
  };

  // Get paginated data for current tab
  const getPaginatedData = (data) => {
    if (!data) return [];
    const startIndex = (currentPage - 1) * cardsPerPage;
    const endIndex = startIndex + cardsPerPage;
    return data.slice(startIndex, endIndex);
  };

  // Get total pages for current tab
  const getTotalPages = (data) => {
    if (!data) return 0;
    return Math.ceil(data.length / cardsPerPage);
  };

  // Reset page when tab changes
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setCurrentPage(1);
  };

  // Render meeting card component - using same design as RunPage
  const renderMeetingCard = (meeting, showDeleteButton = true) => {
    // Calculate the actual date for this meeting
    let meetingDate = null;
    let meetingDateString = null;
    let isToday = false;

    if (meeting.scheduled_date) {
      meetingDate = parseScheduledDate(meeting.scheduled_date);
      meetingDateString = formatDateInMeetingTimezone(meeting.scheduled_date, meeting.timezone || null);

      const today = new Date();
      const meetingDateOnly = new Date(meetingDate.getFullYear(), meetingDate.getMonth(), meetingDate.getDate());
      const todayOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());
      isToday = meetingDateOnly.getTime() === todayOnly.getTime();
    }

    return (
      <MeetingCard
        key={meeting.id}
        event={{
          id: meeting.id,
          name: meeting.name,
          description: meeting.what_to_expect || '',
          image: getPrimaryImageUrl(meeting.images, meeting.image || "https://images.unsplash.com/photo-1598012268972-217e6036c419?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwyfHxydW5uaW5nJTIwY29tbXVuaXR5fGVufDB8fHx8MTc1ODIxMTI1Mnww&ixlib=rb-4.1.0&q=85"),
          time: meeting.scheduled_date ? formatScheduledDateToLocalTime(meeting.scheduled_date, meeting.timezone || null) : 'Time TBD',
          attendees: meeting.current_participants || 0,
          location: meeting.address,
          date: meetingDateString || 'Scheduled',
          difficulty: meeting.difficulty || 'Beginner',
          timeUntil: isToday ? 'Today' : meetingDateString || 'Scheduled'
        }}
        onButtonClick={() => {
          if (showDeleteButton) {
            handleDeleteMeeting(meeting.id);
          }
        }}
        buttonText={(() => {
          if (showDeleteButton) {
            return 'Delete';
          }
          
          // Use database status directly - no time calculations
          return meeting.status.charAt(0).toUpperCase() + meeting.status.slice(1);
        })()}
        showHover={false}
      />
    );
  };

  // Show loading state while checking authentication
  if (loading) {
    return (
      <div className="min-h-screen bg-runcards-background">
        <Navigation />
        <div className="pt-20 flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="w-8 h-8 border-2 border-runcards-border/30 border-t-white rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-runcards-text-secondary">Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  // If no user after loading, redirect handled in useEffect
  if (!user) {
    return null;
  }

  if (!user?.club_id) {
    return (
      <div className="min-h-screen bg-runcards-background">
        <Navigation />
        <div className="pt-20 px-4 py-8">
          <div className="max-w-7xl mx-auto text-center">
            <h1 className="text-runcards-xxl font-runcards-bold text-runcards-text-primary mb-4">My Runs</h1>
            <p className="text-runcards-text-secondary">You need to be a run club owner to view runs.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-runcards-background text-runcards-text-primary">
      <style>{`
        button:focus,
        a:focus,
        input:focus {
          outline: none !important;
          box-shadow: none !important;
        }
        button:focus-visible,
        a:focus-visible,
        input:focus-visible {
          outline: none !important;
          box-shadow: none !important;
        }
      `}</style>
      <Navigation />
      
      {/* Mobile Filter Button - Fixed to top right */}
      <div className="mobile-filter-btn fixed top-4 right-4 z-50">
        <Button
          onClick={() => setShowFilterModal(true)}
          className="bg-runcards-foreground hover:bg-white/30 text-runcards-text-primary border border-runcards-border/20 rounded-full p-3"
        >
          <Filter className="w-5 h-5" />
        </Button>
      </div>

      {/* Hero Section with Background Image */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: "url('/Marcus_Speed_and_Form.png')",
            filter: "grayscale(100%) brightness(0.6)"
          }}
        />
        <div className="absolute inset-0 bg-runcards-background/30" />
        
        <div className="relative z-10 text-center px-4 sm:px-6 max-w-6xl mx-auto pt-44 sm:pt-52 lg:pt-60">
          {/* Main Title */}
          <div className="mb-6">
            <h2 className="text-runcards-title sm:text-4xl md:text-5xl lg:text-6xl font-runcards-bold mb-4 text-runcards-text-primary w-full" style={{letterSpacing: '-0.02em', transform: 'scaleX(1.5)', whiteSpace: 'nowrap'}}>
              MY RUNS
            </h2>
            <p className="text-runcards-xl sm:text-runcards-xxl font-runcards text-runcards-text-tertiary">
              Manage your run club runs and upcoming events.
            </p>
      </div>

          {/* Search Bar */}
          <div className="max-w-2xl mx-auto mb-8">
            <div className="relative">
              <div className="flex">
                <div className="relative flex-1">
                  <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-runcards-text-secondary w-5 h-5" />
                <Input
                  type="text"
                    placeholder="Search by run name or details..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-12 pr-4 py-4 text-runcards-lg bg-runcards-surface border-runcards-border/20 text-runcards-text-primary placeholder-gray-400 focus:bg-runcards-foreground focus:border-runcards-border/40 rounded-l-xl rounded-r-none font-runcards"
                />
              </div>
              <Button
                  onClick={() => {
                    refetchMeetings();
                  }}
                  className="bg-runcards-foreground hover:bg-white/30 text-runcards-text-primary border border-runcards-border/20 px-6 py-4 rounded-r-xl rounded-l-none"
                >
                  <RefreshCw className="w-5 h-5" />
              </Button>
              </div>
            </div>
          </div>

          {/* Schedule a Run Button */}
          <div className="mb-8">
            <Button
              onClick={() => setShowEventForm(true)}
              className="bg-white text-black hover:bg-gray-100 px-8 py-4 rounded-xl font-runcards-bold text-runcards-lg transition-all duration-300 font-runcards"
            >
              <Plus className="w-5 h-5 mr-2" />
              Schedule a Run
            </Button>
          </div>
        </div>
      </section>

      {/* Filter Buttons */}
      <section className="py-16 px-4 sm:px-6 bg-runcards-background">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-wrap justify-center gap-3 mb-12">
            <button
              onClick={() => handleTabChange("upcoming")}
              className={`px-4 py-2 rounded-full text-runcards-sm font-runcards transition-all duration-200 ${
                activeTab === "upcoming" 
                ? 'bg-white text-black'
                : 'bg-runcards-surface text-runcards-text-primary hover:bg-runcards-foreground'
              }`}
              className="font-runcards"
            >
              <Calendar className="w-4 h-4 mr-2 inline" />
              Upcoming
            </button>
            <button
              onClick={() => handleTabChange("cancelled")}
              className={`px-4 py-2 rounded-full text-runcards-sm font-runcards transition-all duration-200 ${
                activeTab === "cancelled" 
                ? 'bg-white text-black'
                : 'bg-runcards-surface text-runcards-text-primary hover:bg-runcards-foreground'
              }`}
              className="font-runcards"
            >
              <X className="w-4 h-4 mr-2 inline" />
              Cancelled
            </button>
            <button
              onClick={() => handleTabChange("recent")}
              className={`px-4 py-2 rounded-full text-runcards-sm font-runcards transition-all duration-200 ${
                activeTab === "recent" 
                ? 'bg-white text-black'
                : 'bg-runcards-surface text-runcards-text-primary hover:bg-runcards-foreground'
              }`}
              className="font-runcards"
            >
              <Clock className="w-4 h-4 mr-2 inline" />
              Recent
            </button>
                  </div>
                  
          {/* Content Section */}
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-12 gap-4">
            <div className="text-center sm:text-left">
              <h2 className="text-runcards-title sm:text-4xl lg:text-5xl font-runcards-bold text-runcards-text-primary mb-4">
                {activeTab === "upcoming" ? "Upcoming Runs" : 
                 activeTab === "cancelled" ? "Cancelled Runs" : "Recent Runs"}
              </h2>
              <p className="text-runcards-text-secondary text-runcards-lg">
                {(() => {
                  const { upcoming, cancelled, recent } = getCategorizedMeetings();
                  const currentData = activeTab === "upcoming" ? upcoming : 
                                    activeTab === "cancelled" ? cancelled : recent;
                  const filteredData = filterMeetingsBySearch(currentData);
                  return `${filteredData.length} ${filteredData.length === 1 ? 'run' : 'runs'} found`;
                })()}
              </p>
            </div>
                  </div>
                  
          {(() => {
            const { upcoming, cancelled, recent } = getCategorizedMeetings();
            const currentData = activeTab === "upcoming" ? upcoming : 
                              activeTab === "cancelled" ? cancelled : recent;
            const searchFilteredData = filterMeetingsBySearch(currentData);
            const filteredData = applyFilters(searchFilteredData);
            const paginatedData = getPaginatedData(filteredData);
            const totalPages = getTotalPages(filteredData);
            
            return (
              <div>
                {meetingsLoading ? (
                  <div className="text-center py-12">
                    <div className="w-8 h-8 border-2 border-runcards-border/30 border-t-white rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="text-runcards-text-secondary">Loading your runs...</p>
                  </div>
                ) : filteredData.length === 0 ? (
                  <div className="text-center py-16">
                    <Trophy className="w-20 h-20 text-runcards-text-primary/40 mx-auto mb-6" />
                    <h3 className="text-runcards-xxl font-runcards-bold text-runcards-text-primary mb-4 font-runcards">
                      {activeTab === "upcoming" ? "No upcoming runs found" :
                       activeTab === "cancelled" ? "No cancelled runs found" : "No recent runs found"}
                    </h3>
                    <p className="text-runcards-text-primary/60 mb-8 text-runcards-lg font-runcards">
                      {activeTab === "upcoming" ? "You don't have any upcoming runs scheduled." :
                       activeTab === "cancelled" ? "You haven't cancelled any runs yet." : "You don't have any completed runs yet."}
                    </p>
                    {activeTab === "upcoming" && (
                      <button 
                        onClick={() => window.location.href = '/runclubs'}
                        className="bg-white text-black px-8 py-3 rounded-xl font-runcards-bold text-runcards-md hover:bg-gray-100 transition-all duration-300 font-runcards"
                      >
                        Create Your First Run
                      </button>
                    )}
                  </div>
                ) : (
                  <>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
                      {paginatedData.map((meeting) => renderMeetingCard(meeting, activeTab === "upcoming"))}
                    </div>
                    {totalPages > 1 && (
                      <div className="flex justify-center items-center mt-8 space-x-2">
                      <Button
                          variant="outline"
                        size="sm"
                          onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                          disabled={currentPage === 1}
                          className="text-runcards-text-primary border-runcards-border/20 hover:bg-runcards-surface"
                        >
                          Previous
                      </Button>
                        <span className="text-runcards-text-primary text-runcards-sm px-4">
                          Page {currentPage} of {totalPages}
                        </span>
                      <Button
                          variant="outline"
                        size="sm"
                          onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                          disabled={currentPage === totalPages}
                          className="text-runcards-text-primary border-runcards-border/20 hover:bg-runcards-surface"
                        >
                          Next
                      </Button>
                      </div>
                    )}
                    </>
                  )}
              </div>
            );
          })()}

        </div>
      </section>

      {/* Delete Meeting Dialog */}
      <AlertDialog open={deleteDialogState.open} onOpenChange={(open) => {
        if (!open) {
          setDeleteDialogState({ open: false, meetingId: null, isRecurring: false });
        }
      }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Meeting</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteDialogState.isRecurring
                ? 'This is a recurring meeting. Would you like to delete just this meeting or the entire series?'
                : 'Are you sure you want to delete this meeting? This action cannot be undone.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            {deleteDialogState.isRecurring ? (
              <>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={() => executeDeleteWithOption(false)}
                  className="bg-gray-500 hover:bg-gray-600"
                >
                  Delete this meeting only
                </AlertDialogAction>
                <AlertDialogAction
                  onClick={() => executeDeleteWithOption(true)}
                  className="bg-red-600 hover:bg-red-700"
                >
                  Delete entire series
                </AlertDialogAction>
              </>
            ) : (
              <>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={() => executeDeleteWithOption(false)}
                  className="bg-red-600 hover:bg-red-700"
                >
                  Yes, Delete
                </AlertDialogAction>
              </>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Event Creation Form Modal */}
      {showEventForm && (
        <div className="fixed inset-0 bg-runcards-background/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-runcards-background border border-runcards-border/20 rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <EventCreationForm 
              onClose={() => setShowEventForm(false)}
              onEventCreated={() => {
                setShowEventForm(false);
                refetchMeetings(); // Refresh the meetings data
                toast.success("Run scheduled successfully!");
              }}
            />
          </div>
        </div>
      )}

      {/* Filter Modal */}
      <FilterModal
        isOpen={showFilterModal}
        onClose={() => setShowFilterModal(false)}
        searchCity={searchCity}
        onSearchCityChange={handleSearchChange}
        onSearchCityFocus={handleInputFocus}
        onSearchCityBlur={handleInputBlur}
        onKeyPress={handleKeyPress}
        suggestions={suggestions}
        showSuggestions={showSuggestions}
        onSuggestionSelect={handleSuggestionSelect}
        selectedFilters={selectedFilters}
        onToggleFilter={toggleFilter}
        selectedTimeFilter={selectedTimeFilter}
        onTimeFilterChange={setSelectedTimeFilter}
        runFilters={runFilters}
        timeFilters={timeFilters}
        onClearFilters={clearAllFilters}
      />
    </div>
  );
};

export default MyEventsPage;